import React, { useMemo, useState } from 'react';
import { SEGMENTS, STAGES, KEEP_OUT_REASONS, istToday } from './deskModel';
import { OwnerAvatar } from './TodayView';

const rupees = (n) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const lakh = (n) => (n >= 100000 ? `₹${(n / 100000).toFixed(1)}L` : rupees(n));
const SOURCE_LABEL = { shopify_company: 'Website', zoho_customer: 'Zoho' };

export default function AccountsView({ leads, team, today, onOpen, onBulk, onKeepOut, onBringBack, onSync, syncing }) {
  const [segment, setSegment] = useState('lapsed');
  const [selected, setSelected] = useState(() => new Set());
  const [assignTo, setAssignTo] = useState('');
  const [keepReason, setKeepReason] = useState('');
  const [busy, setBusy] = useState(false);

  const tabs = useMemo(() => {
    const live = leads.filter((l) => !l.keptOut);
    const accounts = live.filter((l) => l.account?.segment);
    const prospects = live.filter((l) => !l.account?.segment && l.stage !== 'lost');
    const list = Object.entries(SEGMENTS).map(([key, s]) => {
      const rows = accounts.filter((l) => l.account.segment === key);
      return { key, label: s.label, hint: s.hint, rows, value: rows.reduce((n, l) => n + (Number(l.account.totalSpent) || 0), 0) };
    });
    list.push({ key: 'prospects', label: 'Prospects', hint: 'Imported or added leads that never ordered', rows: prospects, value: 0 });
    list.push({ key: 'keptOut', label: 'Kept out', hint: 'Out of the desk for good: no queues, no emails, syncs skip them', rows: leads.filter((l) => l.keptOut), value: 0 });
    return list.filter((t) => t.rows.length);
  }, [leads]);
  const current = tabs.find((t) => t.key === segment) || tabs[0];
  const rows = useMemo(() => [...(current?.rows || [])].sort((a, b) => (b.account?.totalSpent || 0) - (a.account?.totalSpent || 0)), [current]);
  const selectedRows = rows.filter((r) => selected.has(r.id));

  const toggle = (id) => setSelected((prev) => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const pickTop = (n) => setSelected(new Set(rows.filter((r) => !r.owner).slice(0, n).map((r) => r.id)));
  const run = async (patch) => {
    setBusy(true);
    try { await onBulk(selectedRows.map((r) => r.id), patch); setSelected(new Set()); } finally { setBusy(false); }
  };
  const runKeep = async (action) => {
    setBusy(true);
    try { await action(selectedRows.map((r) => r.id)); setSelected(new Set()); setKeepReason(''); } finally { setBusy(false); }
  };
  const inKeptOut = current?.key === 'keptOut';

  if (!tabs.length) {
    return (
      <div className="bd-main">
        <div className="bd-empty">
          <strong style={{ color: 'var(--bd-ink)' }}>No accounts yet.</strong>
          <span>Pull website wholesale companies and Zoho buyers into the desk.</span>
          <button type="button" className="bd-btn bd-btn--primary" disabled={syncing} onClick={onSync}>{syncing ? 'Syncing…' : 'Sync website + Zoho'}</button>
        </div>
      </div>
    );
  }

  return (
    <div className="bd-main">
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-end', gap: 12 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800 }}>Accounts</h1>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--bd-muted)' }}>Everyone who has ordered or registered, grouped by how warm they are. Biggest past spend first.</p>
        </div>
        <button type="button" className="bd-btn bd-btn--ghost" disabled={syncing} onClick={onSync}>{syncing ? 'Syncing…' : 'Sync website + Zoho'}</button>
      </div>

      <div className="bd-segs" role="tablist" aria-label="Segments">
        {tabs.map((t) => (
          <button key={t.key} type="button" role="tab" className="bd-seg" title={t.hint} aria-selected={current.key === t.key} onClick={() => { setSegment(t.key); setSelected(new Set()); }}>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{t.label}</span>
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span className="bd-mono" style={{ fontSize: 20, fontWeight: 600 }}>{t.rows.length}</span>
              {t.value > 0 && <span className="bd-mono" style={{ fontSize: 12, color: 'var(--bd-muted)' }}>{lakh(t.value)}</span>}
            </span>
          </button>
        ))}
      </div>

      {selectedRows.length > 0 ? (
        <div className="bd-bulk" role="region" aria-label="Bulk actions">
          <strong>{selectedRows.length} selected</strong>
          <span style={{ fontSize: 13, color: 'oklch(0.8 0.01 275)' }}>{lakh(selectedRows.reduce((n, r) => n + (Number(r.account?.totalSpent) || 0), 0))} lifetime</span>
          <span style={{ flex: 1 }} />
          {inKeptOut ? (
            <button type="button" className="bd-btn bd-btn--mari" disabled={busy} onClick={() => runKeep(onBringBack)}>Bring back into the desk</button>
          ) : (
            <>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span className="bd-k" style={{ color: 'oklch(0.8 0.01 275)' }}>Keep out</span>
                <select className="bd-select" aria-label="Why keep out" value={keepReason} onChange={(e) => setKeepReason(e.target.value)}>
                  <option value="">Pick a reason</option>
                  {Object.entries(KEEP_OUT_REASONS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </label>
              <button type="button" className="bd-btn bd-btn--ghost" style={{ color: 'oklch(0.96 0.005 275)', borderColor: 'oklch(0.45 0.02 275)' }} disabled={!keepReason || busy} onClick={() => runKeep((ids) => onKeepOut(ids, keepReason))}>Keep out</button>
            </>
          )}
          {!inKeptOut && <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span className="bd-k" style={{ color: 'oklch(0.8 0.01 275)' }}>Assign to</span>
            <select className="bd-select" aria-label="Assign to" value={assignTo} onChange={(e) => setAssignTo(e.target.value)}>
              <option value="">Pick a person</option>
              {team.map((p) => <option key={p.email} value={p.email}>{p.name}</option>)}
            </select>
          </label>}
          {!inKeptOut && <button type="button" className="bd-btn bd-btn--mari" disabled={!assignTo || busy} onClick={() => run({ owner: assignTo })}>Assign</button>}
          {!inKeptOut && <button type="button" className="bd-btn bd-btn--ghost" style={{ color: 'oklch(0.96 0.005 275)', borderColor: 'oklch(0.45 0.02 275)' }} disabled={busy} onClick={() => run({ snoozedUntil: istToday(new Date(Date.now() + 30 * 86400000)) })}>Snooze 30 days</button>}
          <button type="button" className="bd-btn bd-btn--ghost" style={{ color: 'oklch(0.96 0.005 275)', borderColor: 'oklch(0.45 0.02 275)' }} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--bd-muted)' }}>Hand out work:</span>
          <button type="button" className="bd-btn bd-btn--ghost bd-btn--sm" onClick={() => pickTop(10)}>Select top 10 unassigned</button>
          <button type="button" className="bd-btn bd-btn--ghost bd-btn--sm" onClick={() => pickTop(25)}>Top 25</button>
        </div>
      )}

      <div className="bd-table-wrap">
        <table className="bd-table">
          <thead>
            <tr>
              <th><input type="checkbox" aria-label="Select all shown" checked={rows.length > 0 && selectedRows.length === Math.min(rows.length, 200)} onChange={(e) => setSelected(e.target.checked ? new Set(rows.slice(0, 200).map((r) => r.id)) : new Set())} /></th>
              <th>Account</th><th>Source</th><th>Lifetime</th><th>Orders</th><th>Last order</th><th>Buys most</th><th>Stage</th><th>Owner</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 200).map((r) => (
              <tr key={r.id}>
                <td><input type="checkbox" aria-label={`Select ${r.brandName}`} checked={selected.has(r.id)} onChange={() => toggle(r.id)} /></td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <button type="button" className="bd-link" onClick={() => onOpen(r)}>{r.brandName}</button>
                    <span style={{ fontSize: 12, color: 'var(--bd-muted)' }}>{[r.city, r.country].filter(Boolean).join(', ')}</span>
                  </div>
                </td>
                <td>{SOURCE_LABEL[r.source] ? <span className="bd-chip">{SOURCE_LABEL[r.source]}</span> : <span className="bd-chip">Lead</span>}</td>
                <td className="bd-mono" style={{ fontWeight: 600 }}>{r.account ? rupees(r.account.totalSpent) : '—'}</td>
                <td className="bd-mono">{r.account?.ordersCount ?? '—'}</td>
                <td className="bd-mono" style={{ color: r.account?.segment === 'lapsed' ? 'var(--bd-red)' : 'var(--bd-ink-2)' }}>{r.account?.lastOrderAt || '—'}</td>
                <td style={{ color: 'var(--bd-ink-2)' }}>{(r.account?.topFabrics || r.fabrics || []).slice(0, 2).join(', ') || '—'}</td>
                <td>{r.keptOut
                  ? <span className="bd-chip" title={`${r.keptOutBy || ''} ${r.keptOutAt ? r.keptOutAt.slice(0, 10) : ''}`}>{KEEP_OUT_REASONS[r.keptOutReason] || 'Kept out'}</span>
                  : <span className="bd-chip bd-chip--indigo">{STAGES[r.stage]?.label || r.stage}</span>}</td>
                <td><OwnerAvatar email={r.owner} team={team} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length > 200 && <p style={{ margin: 0, padding: 14, fontSize: 13, color: 'var(--bd-muted)' }}>Showing the top 200 of {rows.length}. Search to find others.</p>}
      </div>
    </div>
  );
}
